import React from 'react'
import { Container } from 'typescript-ioc/es5'
import { BetterComponent } from '../../components/better-component/better-component'
import { ModuleLoadingScreen } from '../../components/module-loading-screen/module-loading-screen'
import { Route, Switch } from '../../components/router'
import { GetDomainCode } from './get-domain-code/get-domain-code'
import { PromptEditor } from '../prompts'
import { NotificationDetails } from '../notifications'
import { Dashboard } from '../dashboard'
import Sandbox from '../sandbox/sandbox'
import { TestDetails } from '../notifications/notification-details/notification-test-details'
import { Redirect } from 'react-router'
import Journeys from '../post-auth/journeys'
import { CreateJourney } from '../journeys/create-journey/create-journey'
import { JourneyDetails } from '../journeys/journey-details/journey-details'
import { Domain } from '../post-auth/domain'
import { snakeCase } from '@pushly/models/lib/utils/change-case'
import { SendNotification } from '../notifications/send-notification/send-notification'
import { EditNotification } from '../notifications/edit-notification/edit-notification'
import Notifications from '../post-auth/notifications'
import { Insights } from '../reporting_v2/insights'
import Segments from '../post-auth/segments'
import Segment from '../post-auth/segment'
import { SegmentDetails } from '../segments/segment-details/segment-details'
import { AbilityAction } from '../../enums/ability-action.enum'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { ICaslSubject } from '../../stores/app-ability'
import { DomainDto } from '../../dtos/domain'
import { AppState } from '../../stores/app'
import { observer } from 'mobx-react'
import { ErrorPage } from '../../components/error-page/error-page'
import { buildComponentCheck } from '../../_utils/routing'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { Prompts } from '../prompts/prompts'
import { BuildNamedNotificationTemplate } from '../notifications/named-notification-template/build-named-notification-template'
import { EditNamedNotificationTemplate } from '../notifications/named-notification-template/edit-named-notification-template'
import AppMessages from '../post-auth/app-messages'
import { BuildAppMessage } from '../app-messages/build-app-message/build-app-message'
import { getEnabledDeliveryChannels } from '../../_utils/domain'
import { AppService } from '../../services'
import { AppMessageDetailsView } from '../app-messages/app-message-details/app-message-details-view'
import { FEAT_APP_MESSAGES } from '../../constants'
import { FEAT_FEEDS_MGMT } from '@pushly/aqe/lib/constants'
import { PlatformUserDomainRecord } from '../../models/domain/platform-user-domain-record'
import { FeedDataFormWrapper } from '../../components/feeds/feed-data-form-wrapper'
import { EditJourneyWizard } from '../journeys/edit-journey/edit-journey'
import { FeedDetailsViewWrapper } from '../../components/feeds/feed-details-view-wrapper'
import { FeedsDataViewWrapper } from '../../components/feeds/feeds-data-view-wrapper'
import { DomainNotificationBuilderView } from '../../components/notification-builder/views/domain-notification-builder-view'
import { CatalogPage } from '../catalog/catalog-page'
import { ItemGroupFormPage } from '../catalog/item-groups/item-group-form-page'
import { ItemGroups } from '../catalog/item-groups/item-groups'

interface IProps {}

interface IState {}

@observer
export class DomainFeature extends BetterComponent<IProps, IState> {
    private appState: AppState
    private appService: AppService

    public constructor(props: IProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public render() {
        // Optional chain is used here to account for a rare
        // race condition where ACS has not been initialized
        // due to network activity.
        //
        // TODO: Investigate better state observation to trigger
        //  the a rerender without the need to extract acs.state
        const _ = this.appState.abilityStore.acs?.state

        const pathClassname = snakeCase(this.injectedProps.location?.pathname ?? '').replace(/_?[\d]+_?/g, '_')

        return (
            <div className={`module notifications ${pathClassname}`}>
                <ModuleLoadingScreen />

                <Switch>
                    <Route path={`${this.matchedPath}/__sandbox__`} exact={true} component={Sandbox} />
                    <Route
                        path={`${this.matchedPath}/dashboard`}
                        exact={true}
                        component={buildComponentCheck(Dashboard, [
                            [() => this.accessCheck(AbilityAction.READ, this.appState.currentDomain!)],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/insights`}
                        exact={true}
                        component={buildComponentCheck(Insights, [
                            [() => this.accessCheck(AbilityAction.READ, this.appState.currentDomain!)],
                        ])}
                    />

                    <Route
                        path={`${this.matchedPath}`}
                        exact={true}
                        render={(routeProps) => {
                            const Comp = buildComponentCheck(Domain, [
                                [() => this.accessCheck(AbilityAction.UPDATE, this.appState.currentDomain!)],
                            ])
                            return <Comp {...routeProps} />
                        }}
                    />
                    <Route
                        path={`${this.matchedPath}/code`}
                        exact={true}
                        component={buildComponentCheck(GetDomainCode, [
                            [() => this.accessCheck(AbilityAction.READ, this.appState.currentDomain!)],
                        ])}
                    />

                    {/* // ─── Notifications ────────────────────────── */}
                    <Route
                        path={[
                            `${this.matchedPath}/notifications`,
                            `${this.matchedPath}/notifications/drafts`,
                            `${this.matchedPath}/notifications/templates`,
                            `${this.matchedPath}/notifications/feeds`,
                        ]}
                        exact={true}
                        component={buildComponentCheck(Notifications, [
                            [() => this.accessCheck(AbilityAction.READ, SubjectEntity.NOTIFICATION)],
                        ])}
                    />
                    <Redirect
                        from={`${this.matchedPath}/notifications/:notificationId([0-9]+)`}
                        to={`${this.matchedPath}/notifications/:notificationId([0-9]+)/summary`}
                        exact={true}
                    />
                    <Route
                        path={`${this.matchedPath}/notifications/:notificationId([0-9]+)/:tab([\\w]+)`}
                        exact={true}
                        component={buildComponentCheck(NotificationDetails, [
                            [() => this.accessCheck(AbilityAction.READ, SubjectEntity.NOTIFICATION)],
                        ])}
                    />
                    <Redirect
                        from={`${this.matchedPath}/notifications/test/:testId([0-9]+)`}
                        to={`${this.matchedPath}/notifications/test/:testId([0-9]+)/summary`}
                        exact={true}
                    />
                    <Route
                        path={`${this.matchedPath}/notifications/test/:testId([0-9]+)/:tab([\\w]+)`}
                        exact={true}
                        component={buildComponentCheck(TestDetails, [
                            [() => this.accessCheck(AbilityAction.READ, SubjectEntity.NOTIFICATION_TEST)],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/notifications/new`}
                        exact={true}
                        component={buildComponentCheck(SendNotification, [
                            [() => this.accessCheck(AbilityAction.CREATE, SubjectEntity.NOTIFICATION)],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/notifications/edit/:notificationId([0-9]+)`}
                        exact={true}
                        component={buildComponentCheck(EditNotification, [
                            [() => this.accessCheck(AbilityAction.UPDATE, SubjectEntity.NOTIFICATION)],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/notifications/templates/new`}
                        exact={true}
                        component={buildComponentCheck(BuildNamedNotificationTemplate, [
                            [() => this.accessCheck(AbilityAction.CREATE, SubjectEntity.NOTIFICATION)],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/notifications/templates/edit/:templateId([0-9]+)`}
                        exact={true}
                        component={buildComponentCheck(EditNamedNotificationTemplate, [
                            [() => this.accessCheck(AbilityAction.UPDATE, SubjectEntity.NOTIFICATION)],
                        ])}
                    />
                    {/* // ─── Feeds (top-level list + detail/create/edit) ────────────────────────── */}
                    {/*
                     * Standalone feed routes. Feeds was promoted to a top-level
                     * nav item; the list previously only existed as the Notifications
                     * "feeds" tab (now the "Feeds has moved" redirect card). All feed
                     * routes are gated the same way the Feeds nav is — FEED access
                     * (+ FEAT_FEEDS_MGMT where management is required).
                     * `FeedsDataViewWrapper` takes `{ domain, appState }` props (it was
                     * a tab body, not a route), so the list route supplies them from
                     * `this.appState` rather than from RouteComponentProps; the
                     * detail/create/edit wrappers consume RouteComponentProps directly.
                     * The detail/create/edit routes now live here under /feeds/*; the
                     * legacy /notifications/feeds/* URLs are redirected below for
                     * backward compatibility.
                     */}
                    <Route
                        path={`${this.matchedPath}/feeds`}
                        exact={true}
                        render={(routeProps) => {
                            const Comp = buildComponentCheck(FeedsDataViewWrapper, [
                                [() => this.accessCheck(AbilityAction.READ, SubjectEntity.FEED)],
                                [
                                    () => this.flagsCheck(this.appState.currentDomain!.flags, FEAT_FEEDS_MGMT),
                                    this.feedsManagementNotImplementedComponent,
                                ],
                            ])
                            return (
                                <Comp {...routeProps} domain={this.appState.currentDomain!} appState={this.appState} />
                            )
                        }}
                    />
                    <Route
                        path={`${this.matchedPath}/feeds/new`}
                        exact={true}
                        component={buildComponentCheck(FeedDataFormWrapper, [
                            [() => this.accessCheck(AbilityAction.CREATE, SubjectEntity.FEED)],
                            [
                                () => this.flagsCheck(this.appState.currentDomain!.flags, FEAT_FEEDS_MGMT),
                                this.feedsManagementNotImplementedComponent,
                            ],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/feeds/:feedId([0-9]+)`}
                        exact={true}
                        component={buildComponentCheck(FeedDetailsViewWrapper, [
                            [() => this.accessCheck(AbilityAction.READ, SubjectEntity.FEED)],
                            // Detail is a read route but is still flag-gated to match the list
                            // route and the Feeds nav; without this a domain lacking the flag
                            // could deep-link straight to /feeds/:id and bypass the list gate.
                            [
                                () => this.flagsCheck(this.appState.currentDomain!.flags, FEAT_FEEDS_MGMT),
                                this.feedsManagementNotImplementedComponent,
                            ],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/feeds/:feedId([0-9]+)/edit`}
                        exact={true}
                        component={buildComponentCheck(FeedDataFormWrapper, [
                            [() => this.accessCheck(AbilityAction.UPDATE, SubjectEntity.FEED)],
                            [
                                () => this.flagsCheck(this.appState.currentDomain!.flags, FEAT_FEEDS_MGMT),
                                this.feedsManagementNotImplementedComponent,
                            ],
                        ])}
                    />

                    {/* Legacy /notifications/feeds/* URLs → relocated /feeds/* (backward compatibility) */}
                    <Redirect
                        from={`${this.matchedPath}/notifications/feeds/:feedId([0-9]+)`}
                        to={`${this.matchedPath}/feeds/:feedId([0-9]+)`}
                        exact={true}
                    />
                    <Redirect
                        from={`${this.matchedPath}/notifications/feeds/new`}
                        to={`${this.matchedPath}/feeds/new`}
                        exact={true}
                    />
                    <Redirect
                        from={`${this.matchedPath}/notifications/feeds/:feedId([0-9]+)/edit`}
                        to={`${this.matchedPath}/feeds/:feedId([0-9]+)/edit`}
                        exact={true}
                    />

                    {/* // ─── App Messages ────────────────────────── */}
                    <Route
                        path={[`${this.matchedPath}/app-messages`, `${this.matchedPath}/app-messages/templates`]}
                        exact={true}
                        component={buildComponentCheck(AppMessages, [
                            [() => this.accessCheck(AbilityAction.READ, SubjectEntity.APP_MESSAGE)],
                            [
                                () => this.flagsCheck(this.appState.currentDomain!.flags, FEAT_APP_MESSAGES),
                                this.appMessagesNotImplementedComponent,
                            ],
                            [
                                () => this.channelCheck([DeliveryChannel.NATIVE_IOS, DeliveryChannel.NATIVE_ANDROID]),
                                this.nativeNotImplementedComponent,
                            ],
                        ])}
                    />

                    <Route
                        path={[
                            `${this.matchedPath}/app-messages/new`,
                            `${this.matchedPath}/app-messages/templates/new`,
                            `${this.matchedPath}/app-messages/:appMessageId([0-9]+)/edit`,
                            `${this.matchedPath}/app-messages/templates/:appMessageTemplateId([0-9]+)/edit`,
                        ]}
                        exact={true}
                        component={buildComponentCheck(BuildAppMessage, [
                            [() => this.accessCheck(AbilityAction.CREATE, SubjectEntity.APP_MESSAGE)],
                            [
                                () => this.flagsCheck(this.appState.currentDomain!.flags, FEAT_APP_MESSAGES),
                                this.appMessagesNotImplementedComponent,
                            ],
                            [
                                () =>
                                    this.channelCheck(
                                        [DeliveryChannel.NATIVE_IOS, DeliveryChannel.NATIVE_ANDROID],
                                        true,
                                    ),
                                this.appMessageNativeRequiredComponent,
                            ],
                        ])}
                    />

                    <Redirect
                        from={`${this.matchedPath}/app-messages/:appMessageId([0-9]+)`}
                        to={`${this.matchedPath}/app-messages/:appMessageId([0-9]+)/summary`}
                        exact={true}
                    />

                    <Route
                        path={`${this.matchedPath}/app-messages/:appMessageId([0-9]+)/:tab([\\w]+)`}
                        exact={true}
                        component={buildComponentCheck(AppMessageDetailsView, [
                            [() => this.accessCheck(AbilityAction.READ, SubjectEntity.APP_MESSAGE)],
                            [
                                () => this.flagsCheck(this.appState.currentDomain!.flags, FEAT_APP_MESSAGES),
                                this.appMessagesNotImplementedComponent,
                            ],
                            [
                                () => this.channelCheck([DeliveryChannel.NATIVE_IOS, DeliveryChannel.NATIVE_ANDROID]),
                                this.nativeNotImplementedComponent,
                            ],
                        ])}
                    />

                    {/* // ─── Journey routes (canonical) ──────────────── */}
                    <Route
                        path={`${this.matchedPath}/journeys`}
                        exact={true}
                        component={buildComponentCheck(Journeys, [
                            [() => this.accessCheck(AbilityAction.READ, SubjectEntity.CAMPAIGN)],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/journeys/new`}
                        exact={true}
                        component={buildComponentCheck(CreateJourney, [
                            [() => this.accessCheck(AbilityAction.CREATE, SubjectEntity.CAMPAIGN)],
                        ])}
                    />
                    <Route
                        path={[
                            `${this.matchedPath}/journeys/:journeyId([0-9]+)/builder`,
                            `${this.matchedPath}/journeys/:journeyId([0-9]+)/configuration`,
                        ]}
                        exact={true}
                        component={buildComponentCheck(EditJourneyWizard, [
                            [() => this.accessCheck(AbilityAction.UPDATE, SubjectEntity.CAMPAIGN)],
                        ])}
                    />
                    <Redirect
                        from={`${this.matchedPath}/journeys/:journeyId([0-9]+)/summary`}
                        to={`${this.matchedPath}/journeys/:journeyId([0-9]+)`}
                        exact={true}
                    />
                    <Redirect
                        from={`${this.matchedPath}/journeys/:journeyId([0-9]+)/:tab([\\w]+)`}
                        to={`${this.matchedPath}/journeys/:journeyId([0-9]+)`}
                        exact={true}
                    />
                    <Route
                        path={`${this.matchedPath}/journeys/:journeyId([0-9]+)`}
                        exact={true}
                        component={buildComponentCheck(JourneyDetails, [
                            [() => this.accessCheck(AbilityAction.READ, SubjectEntity.CAMPAIGN)],
                        ])}
                    />

                    {/* Legacy /campaigns redirects to /journeys equivalents */}
                    <Redirect
                        from={`${this.matchedPath}/campaigns/new`}
                        to={`${this.matchedPath}/journeys/new`}
                        exact={true}
                    />
                    <Redirect
                        from={`${this.matchedPath}/campaigns/:journeyId([0-9]+)/journey-beta`}
                        to={`${this.matchedPath}/journeys/:journeyId([0-9]+)/builder`}
                        exact={true}
                    />
                    <Redirect
                        from={`${this.matchedPath}/campaigns/:journeyId([0-9]+)/:tab([\\w]+)`}
                        to={`${this.matchedPath}/journeys/:journeyId([0-9]+)/:tab`}
                        exact={true}
                    />
                    <Redirect
                        from={`${this.matchedPath}/campaigns/:journeyId([0-9]+)`}
                        to={`${this.matchedPath}/journeys/:journeyId([0-9]+)/summary`}
                        exact={true}
                    />
                    <Redirect from={`${this.matchedPath}/campaigns`} to={`${this.matchedPath}/journeys`} exact={true} />

                    {/* // ─── Catalogs ────────────────────────── */}
                    <Route
                        path={[
                            `${this.matchedPath}/catalogs/item-groups/new`,
                            `${this.matchedPath}/catalogs/item-groups/:groupId([0-9]+)/edit`,
                        ]}
                        exact={true}
                        component={buildComponentCheck(ItemGroups, [
                            [() => this.accessCheck(AbilityAction.CREATE, this.appState.currentDomain!)],
                        ])}
                    />
                    <Route
                        path={[`${this.matchedPath}/catalogs`, `${this.matchedPath}/catalogs/item-groups`]}
                        exact={true}
                        component={buildComponentCheck(CatalogPage, [
                            [() => this.accessCheck(AbilityAction.READ, this.appState.currentDomain!)],
                        ])}
                    />

                    {/* // ─── Prompts ────────────────────────── */}
                    <Route
                        path={[
                            `${this.matchedPath}/prompts`,
                            `${this.matchedPath}/prompts/native_ios`,
                            `${this.matchedPath}/prompts/native_android`,
                        ]}
                        exact={true}
                        component={buildComponentCheck(Prompts, [
                            [() => this.accessCheck(AbilityAction.READ, SubjectEntity.PROMPT)],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/prompts/new`}
                        exact={true}
                        component={buildComponentCheck(PromptEditor, [
                            [() => this.accessCheck(AbilityAction.CREATE, SubjectEntity.PROMPT)],
                            [() => this.channelCheck([DeliveryChannel.WEB]), this.webNotImplementedComponent],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/prompts/:promptId([0-9]+)`}
                        exact={true}
                        component={buildComponentCheck(PromptEditor, [
                            [() => this.accessCheck(AbilityAction.UPDATE, SubjectEntity.PROMPT)],
                            [() => this.channelCheck([DeliveryChannel.WEB]), this.webNotImplementedComponent],
                        ])}
                    />

                    {/* // ─── Segments ────────────────────────── */}
                    <Route
                        path={`${this.matchedPath}/segments/new`}
                        exact={true}
                        component={buildComponentCheck(Segment, [
                            [() => this.accessCheck(AbilityAction.CREATE, SubjectEntity.SEGMENT)],
                        ])}
                    />
                    <Route
                        path={[`${this.matchedPath}/segments`, `${this.matchedPath}/segments/compare`]}
                        exact={true}
                        component={buildComponentCheck(Segments, [
                            [() => this.accessCheck(AbilityAction.READ, SubjectEntity.SEGMENT)],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/segments/:segmentId([0-9]+)`}
                        exact={true}
                        component={buildComponentCheck(Segment, [
                            [() => this.accessCheck(AbilityAction.UPDATE, SubjectEntity.SEGMENT)],
                        ])}
                    />
                    <Route
                        path={`${this.matchedPath}/segments/:segmentId([0-9]+)/:tab([\\w]+)`}
                        exact={true}
                        component={buildComponentCheck(SegmentDetails, [
                            [() => this.accessCheck(AbilityAction.READ, SubjectEntity.SEGMENT)],
                        ])}
                    />
                </Switch>
            </div>
        )
    }

    protected get webNotImplementedComponent() {
        return () =>
            ErrorPage({
                errorCode: 403,
                hideBackgroundCode: true,
                label: 'This section of the platform is not available for domains that do not implement Web Push.',
            })
    }

    protected get nativeNotImplementedComponent() {
        return () =>
            ErrorPage({
                errorCode: 403,
                hideBackgroundCode: true,
                label: 'This section of the platform is not available for domains that do not implement Native Push.',
            })
    }

    protected get appMessagesNotImplementedComponent() {
        return () =>
            ErrorPage({
                errorCode: 403,
                hideBackgroundCode: true,
                label: 'This section of the platform is not available without the App Message feature enabled. To enable this feature, please contact your Account Manager.',
            })
    }

    protected get feedsManagementNotImplementedComponent() {
        return () =>
            ErrorPage({
                errorCode: 403,
                hideBackgroundCode: true,
                label: 'This section of the platform is not available without Feeds Management enabled. To enable this feature, please contact your Account Manager.',
            })
    }

    protected get appMessageNativeRequiredComponent() {
        const label = (
            <span>
                At least one of the Native Apple or Android&nbsp;
                <a onClick={this.handleSendIntegrationLinkClick}>Send Integrations</a> must be configured and enabled to
                create App Messages.
            </span>
        )
        return () =>
            ErrorPage({
                errorCode: 403,
                hideBackgroundCode: true,
                label,
            })
    }

    protected channelCheck = (channels: DeliveryChannel[], activeOnly: boolean = false) => {
        return channels.some((channel) =>
            getEnabledDeliveryChannels(this.appState.currentDomain!, activeOnly).includes(
                DeliveryChannel.fromString(channel)!,
            ),
        )
    }

    protected flagsCheck = (flags: string[], flag: string) => {
        return flags.includes(flag)
    }

    protected accessCheck = <T extends SubjectEntity>(
        action: AbilityAction,
        check: T | ICaslSubject<T> | DomainDto | PlatformUserDomainRecord,
    ) => {
        const isDomainCheck =
            check instanceof DomainDto || check instanceof PlatformUserDomainRecord || check === SubjectEntity.DOMAIN
        const entity = isDomainCheck ? check : this.appState.abilityStore.getDomainOwnedIdentityFor(check as T)

        return this.appState.abilityStore.can(action, entity as ICaslSubject<T> | DomainDto)
    }

    protected handleSendIntegrationLinkClick = () => {
        const historyState = {
            containerId: 'send-integrations',
            returnTo: window.location.pathname,
        }

        this.appService.routeWithinDomain('#settings', false, historyState)
    }
}
